import React, { useEffect, useState } from "react";
import { Field } from "formik";
import {
  TextField as MUITextField,
  FormControl,
  FormHelperText,
  Box,
  Button,
  MenuItem,
  Chip,
  TextareaAutosize,
} from "@mui/material";
import { styled } from "@mui/system";
import FormikFileUpload from "@/components/forms/FormikFileUpload";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";

// Custom styled label
const StyledLabel = styled("label")({
  fontSize: "1rem",
  fontWeight: "500",
  color: "#333",
  marginBottom: "8px",
  display: "block",
});

const StyledTextField = styled(MUITextField)({
  height: "48px",
  width: "100%",
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "8px",
  },
});

const StyledTextareaAutosize = styled(TextareaAutosize)({
  width: "100%",
  borderRadius: "8px",
  border: "1px solid #D8D8D8",
  padding: "15px 8px",
  boxSizing: "border-box",
  fontSize: "1rem",
  resize: "vertical",
  fontFamily: "Lato, sans-serif",
});

// Styled asterisk for required fields
const RequiredAsterisk = styled("span")({
  color: "red",
  marginLeft: "4px",
});

function DynamicField({
  label,
  name,
  type,
  options = [],
  placeholder,
  required,
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const showError = meta.touched && Boolean(meta.error);
        const labelWithAsterisk = (
          <>
            {label === "Role Type" ? "Sequence" : label}
            {required && <RequiredAsterisk>*</RequiredAsterisk>}
          </>
        );

        useEffect(() => {
          if (name === "roleType") {
            if (!form.values.roles || form.values.roles.length === 0) {
              form.setFieldValue("roles", [{ roleType: "", roleSequence: "" }]);
            }
            if (form.values.roleType.length > 0) {
              form.setFieldValue("roles", form.values.roleType);
            }
          }
        }, []);

        return (
          <FormControl margin="normal" error={showError} fullWidth>
            <StyledLabel htmlFor={name}>{labelWithAsterisk}</StyledLabel>

            {name === "roleType" ? (
              <>
                {form.values.roles?.map((role, index) => (
                  <Box key={index} display="flex" gap={2} alignItems="center">
                    {/* Role Type */}
                    <StyledTextField
                      type="text"
                      placeholder="Role Type"
                      name={`roles[${index}].roleType`}
                      value={role.roleType || ""}
                      onChange={(e) => {
                        const newRoles = [...form.values.roles];
                        newRoles[index].roleType = e.target.value;
                        form.setFieldValue("roles", newRoles);
                      }}
                      variant="outlined"
                      fullWidth
                      sx={{ marginBottom: 1 }}
                    />

                    {/* Role Sequence */}
                    <StyledTextField
                      type="text"
                      placeholder="Role Sequence"
                      name={`roles[${index}].roleSequence`}
                      value={role.roleSequence || ""}
                      onChange={(e) => {
                        const newRoles = [...form.values.roles];
                        newRoles[index].roleSequence = e.target.value;
                        form.setFieldValue("roles", newRoles);
                      }}
                      variant="outlined"
                      fullWidth
                      sx={{ marginBottom: 1 }}
                    />

                    {/* Delete Button - Disabled Initially */}
                    <Button
                      onClick={() => {
                        const newRoles = form.values.roles.filter(
                          (_, i) => i !== index
                        );
                        form.setFieldValue("roles", newRoles);
                      }}
                      disabled={form.values.roles.length <= 1} // Disable if only one row exists
                    >
                      <DeleteOutlinedIcon />
                    </Button>
                  </Box>
                ))}

                {/* Add Role Button */}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      form.setFieldValue("roles", [
                        ...(form.values.roles || []),
                        { roleType: "", roleSequence: "" },
                      ])
                    }
                    sx={{ width: "15%" }}
                  >
                    + Add
                  </Button>
                </Box>
              </>
            ) : type === "dropdown" ? (
              <MUITextField
                select
                id={name}
                placeholder={placeholder}
                {...field}
                value={field.value?.organizationCode || "Select Organization"} // Handle undefined value safely
                error={showError}
                variant="outlined"
                fullWidth
                onChange={(event) => {
                  const selectedValue = event.target.value;
                  const selectedOption = options.find(
                    (option) => option.organizationCode === selectedValue
                  ); // Ensure selectedOption exists

                  form.setFieldValue(name, selectedOption ?? null); // Set full object or null
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "48px !important",
                  },
                }}
              >
                <MenuItem value="Select Organization" disabled>
                  Select Organization
                </MenuItem>
                {options.map((option, index) => (
                  <MenuItem key={index} value={option.organizationCode}>
                    {option.organizationDetail}
                  </MenuItem>
                ))}
              </MUITextField>
            ) : type === "file" ? (
              <FormikFileUpload
                name={name}
                placeholder={placeholder}
                startIcon={<CloudUploadOutlinedIcon />}
                acceptedFileFormats={["image/png", "image/jpeg", "image/jpg"]}
                // handleChange={(e) => {
                //   const { name, value } = e.target;
                //   form.setFieldValue(name, value);
                // }}
              />
            ) : type === "dropdownProbation" ? (
              <MUITextField
                select
                id={name}
                placeholder={placeholder}
                {...field}
                value={field.value?.value ?? "Select Status"} // Handle undefined value safely
                error={showError}
                variant="outlined"
                fullWidth
                onChange={(event) => {
                  const selectedValue = event.target.value;
                  const selectedOption = options.find(
                    (option) => option.value === (selectedValue === "true") // Ensure proper boolean comparison
                  );

                  form.setFieldValue(name, selectedOption ?? null); // Set full object or null
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "48px !important",
                  },
                }}
              >
                <MenuItem value="Select Status" disabled>
                  {"Select Status"}
                </MenuItem>
                {options.map((option, index) => (
                  <MenuItem key={index} value={option.value.toString()}>
                    {option.label}
                  </MenuItem>
                ))}
              </MUITextField>
            ) : type === "dropdownMultiple" ? (
              <MUITextField
                select
                id={name}
                placeholder={placeholder}
                value={
                  Array.isArray(field.value)
                    ? field.value.map((item) => item.organizationCode)
                    : ["Select Organization Mapping"]
                } // Extract organizationCode
                onChange={(event) => {
                  const selectedValues = event.target.value;
                  const selectedObjects = options.filter((opt) =>
                    selectedValues.includes(opt.organizationCode)
                  );
                  form.setFieldValue(name, selectedObjects); // Store full object in form
                }}
                error={showError}
                variant="outlined"
                fullWidth
                SelectProps={{
                  multiple: true, // Enable multi-select
                  renderValue: (selected) =>
                    selected.length === 0 ? (
                      <MenuItem value="Select Organization Mapping">
                        {"Select Organization Mapping"}
                      </MenuItem>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "5px",
                        }}
                      >
                        {selected.map((code) => {
                          const selectedOption = options.find(
                            (opt) => opt.organizationCode === code
                          );
                          return (
                            <Chip
                              key={code}
                              label={selectedOption?.organizationDetail || code}
                              sx={{ margin: "2px", height: "30px" }}
                              onDelete={() => {
                                const newValue = selected.filter(
                                  (v) => v !== code
                                );
                                form.setFieldValue(
                                  name,
                                  options.filter((opt) =>
                                    newValue.includes(opt.organizationCode)
                                  )
                                );
                              }}
                              deleteIcon={
                                <CancelIcon
                                  onMouseDown={(event) =>
                                    event.stopPropagation()
                                  }
                                />
                              }
                            />
                          );
                        })}
                      </div>
                    ),
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "48px !important",
                  },
                }}
              >
                {options.map((option, index) => (
                  <MenuItem key={index} value={option.organizationCode}>
                    {option.organizationDetail}
                  </MenuItem>
                ))}
              </MUITextField>
            ) : type === "probationDropDownMultiple" ? (
              <MUITextField
                select
                id={name}
                placeholder={placeholder}
                value={
                  Array.isArray(field.value) ? field.value : ["Select Months"]
                } // Ensure it's an array
                onChange={(event) => {
                  const selectedValues = event.target.value; // Directly store selected string values
                  form.setFieldValue(name, selectedValues);
                }}
                error={showError}
                variant="outlined"
                fullWidth
                SelectProps={{
                  multiple: true, // Enable multi-select
                  renderValue: (selected) => (
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}
                    >
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          onMouseDown={(event) => event.stopPropagation()} // Prevent dropdown from opening
                          onDelete={(event) => {
                            event.stopPropagation(); // Prevent dropdown from opening when clicking delete
                            const updatedValues = field.value.filter(
                              (v) => v !== value
                            );
                            form.setFieldValue(name, updatedValues);
                          }}
                        />
                      ))}
                    </div>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "48px !important",
                  },
                }}
              >
                <MenuItem value="Select Months">{"Select Months"}</MenuItem>
                {options.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </MUITextField>
            ) : name === "password" ? (
              <StyledTextField
                id={name}
                type={showPassword ? "text" : "password"} // Toggle between text and password
                placeholder={placeholder}
                {...field}
                error={showError}
                variant="outlined"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ) : type === "number" ? (
              <StyledTextField
                id={name}
                type="text"
                placeholder={placeholder}
                {...field}
                error={showError}
                variant="outlined"
                fullWidth
                inputProps={{ min: 1, max: 10 }}
                onChange={(e) => {
                  let value = e.target.value;

                  value = value.replace(/[^0-9]/g, "");

                  while (parseInt(value) > 10) {
                    value = value.slice(0, -1); // Remove last digit until it's ≤ 10
                  }

                  form.setFieldValue(name, value);
                }}
              />
            ) : type === "textarea" ? (
              <StyledTextareaAutosize
                id={name}
                {...field}
                error={showError}
                minRows={1}
                placeholder={placeholder}
                onChange={(event) => {
                  form.setFieldValue(name, event.target.value);
                }}
              />
            ) : type === "contactNumber" ? (
              <StyledTextField
                id={name}
                type={type}
                placeholder={placeholder}
                {...field}
                error={showError}
                variant="outlined"
                fullWidth
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/[^0-9]/g, "");
                  if (value.length > 10) {
                    value = value.slice(0, 10);
                  }
                  form.setFieldValue(name, value);
                }}
              />
            ) : name === "mobileNumber" ? (
              <StyledTextField
                id={name}
                type={type}
                placeholder={placeholder}
                {...field}
                error={showError}
                variant="outlined"
                fullWidth
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/[^0-9]/g, "");
                  if (value.length > 10) {
                    value = value.slice(0, 10);
                  }
                  form.setFieldValue(name, value);
                }}
              />
            ) : (
              <StyledTextField
                id={name}
                type={type}
                placeholder={placeholder}
                {...field}
                error={showError}
                variant="outlined"
                fullWidth
              />
            )}

            {showError && <FormHelperText>{meta.error}</FormHelperText>}
          </FormControl>
        );
      }}
    </Field>
  );
}

export default DynamicField;
